import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, BookOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTour } from "./TourProvider";
import { featureById, TIER_LABEL } from "@/lib/product-tour";
import { cardPosition } from "@/lib/tour-position";

/**
 * The spotlight.
 *
 * The dimming is ONE element, not four panels around a hole: a div sized to the
 * target with `box-shadow: 0 0 0 9999px` paints everything outside itself and
 * leaves the target lit. That is why there is no library here — the effect is a
 * box-shadow, and adding a tour dependency to draw it would undo the bundle
 * work that took this app from 226 kB to 150 kB.
 *
 * The interesting cases are all "the anchor is not where you think":
 *   - Not in the DOM (renamed markup, a page that has not finished loading) →
 *     the step renders centred with no spotlight rather than pointing at the
 *     top-left corner or throwing. A tour must degrade to a slideshow, never to
 *     a broken page.
 *   - Off screen → scrolled into view before measuring.
 *   - Moved after measuring (sticky headers, layout shift, resize) → re-measured
 *     on scroll and resize.
 *
 * Daybreak: the scrim is night, so everything drawn ON the scrim uses the white
 * ramp. The card is lit ground and uses the stone ramp. `dawn` is the lit edge
 * on the card, never a status colour.
 */

const PAD = 8; // breathing room between the lit element and the shadow edge

function useAnchorRect(selector) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!selector) {
      setRect(null);
      return undefined;
    }
    let frame = 0;

    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // A zero-sized box is a hidden element (the sidebar below `md`), which is
      // indistinguishable from "missing" for our purposes and should degrade
      // the same way rather than spotlighting nothing.
      if (r.width === 0 && r.height === 0) {
        setRect(null);
        return;
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const el = document.querySelector(selector);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ block: "center", inline: "nearest" });
    }
    // One frame later: scrollIntoView has not settled by the time it returns.
    frame = requestAnimationFrame(measure);

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector]);

  return rect;
}

export default function TourOverlay() {
  const tour = useTour();
  const cardRef = useRef(null);
  const step = tour?.step ?? null;
  const rect = useAnchorRect(step?.anchor ?? null);

  // Move focus to the card as the step changes so a keyboard or screen-reader
  // user hears the new step instead of staying wherever they were.
  useEffect(() => {
    if (step) cardRef.current?.focus();
  }, [step]);

  if (!tour?.running || !step) return null;

  const feature = step.feature ? featureById(step.feature) : null;
  const pos = cardPosition(rect);
  const { index, total, isFirst, isLast, next, prev, stop } = tour;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {/* The scrim. When there is no anchor it is a plain wash; when there is
          one it is the shadow cast by the lit box below. */}
      {rect ? (
        <div
          data-testid="tour-spotlight"
          aria-hidden="true"
          className="absolute rounded-xl pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(12, 10, 9, 0.72)",
            outline: "2px solid rgba(255, 255, 255, 0.55)",
          }}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-stone-950/72" />
      )}

      {/* Clicking the scrim leaves. Placed under the card so the card's own
          clicks do not fall through to it. */}
      <button
        type="button"
        aria-label="Close the tour"
        className="absolute inset-0 w-full h-full cursor-default"
        onClick={() => stop()}
        tabIndex={-1}
      />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-step-title"
        tabIndex={-1}
        data-testid="tour-card"
        className={
          "absolute w-[min(380px,calc(100vw-2rem))] rounded-2xl bg-paper border border-sand " +
          "shadow-2xl p-5 outline-none " +
          (pos.centred ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "")
        }
        style={pos.centred ? undefined : { left: pos.left, top: pos.top }}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-600">
            Step {index + 1} of {total}
          </div>
          <button
            type="button"
            onClick={() => stop()}
            aria-label="Close the tour"
            className="-mt-1 -mr-1 p-1 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-sand/60"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h2
          id="tour-step-title"
          className="text-lg font-bold font-heading text-stone-950 leading-snug"
        >
          {step.title}
        </h2>

        <p className="mt-2 text-sm text-stone-700 leading-relaxed">{step.body}</p>

        {feature && feature.tier !== "free" && (
          // Named, not hidden. Someone on Free should learn the product's shape
          // and what an upgrade actually buys — concealing it would make the
          // tour less useful to the people who most need the tour.
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-sand bg-sand/40 px-2.5 py-1 text-[11px] font-semibold text-stone-700">
            {TIER_LABEL[feature.tier]} feature
          </div>
        )}

        {feature && (
          <Link
            to={`/docs/${feature.slug}`}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-900"
            onClick={() => stop()}
          >
            <BookOpen className="w-3.5 h-3.5" />
            Read the full docs for {feature.name}
          </Link>
        )}

        {step.docsCta && (
          <Link
            to="/docs"
            onClick={() => stop({ completed: true })}
            className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-stone-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-stone-800"
          >
            <BookOpen className="w-4 h-4" />
            Browse the documentation
          </Link>
        )}

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => stop()}
            className="text-xs font-semibold text-stone-500 hover:text-stone-800"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={prev} aria-label="Previous step">
                <ArrowLeft className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" onClick={next}>
              {isLast ? "Finish" : "Next"}
              {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
